/**
 * Retro-agreement selector — merges the two reader endpoints into one ordered,
 * verdict-tagged finding set for the surfacer stage.
 *
 *  - MISSED findings come from the surfacer endpoint (`useSurfacerCandidates`),
 *    which is the server-authoritative curated set: `ai_only` at the recommended
 *    OP, filtered to clinically significant categories. We do NOT re-derive this
 *    on the FE — the backend owns the curation.
 *  - AGREED findings come from the comparison matrix (`useReporterComparison`),
 *    classified at the recommended OP via the shared `getClassification` (the
 *    no-silent-OP-fallback SSoT), filtered to the same significant categories so
 *    the gallery stays symmetric with the missed set.
 *
 * `report_only` / `neither` are intentionally dropped — this stage shows what the
 * AI caught (agreed) and what the report missed, not what the AI itself missed.
 */
import type { AdjudicationVerdict, SurfacerResponse } from "../../hooks/useSurfacerCandidates";
import type { ReporterComparisonResponse } from "../../hooks/useReporterComparisonQueries";
import { getClassification } from "../../utils/comparisonClassification";

export type RetroVerdict = "missed" | "agreed";

export interface RetroFinding {
  verdict: RetroVerdict;
  // Full condition slug, e.g. "hepatobiliary/hepatic_lesion".
  condition: string;
  category: string | null;
  // Key slice for the clean preview; null when the study isn't canonicalized.
  topSlice: string | null;
  // Span to highlight in the signed report. null for missed findings — by
  // definition they are absent from the report, so nothing highlights.
  reportExcerpt: string | null;
  // One line under the tile: the miss phrase (missed) or the report quote (agreed).
  contextPhrase: string;
  // OP advancement (missed only; agreed carries probability instead).
  advancement: number | null;
  // Cohort percentile of the finding's probability (0..1). Surfacer/missed only
  // — the comparison matrix doesn't carry it for agreed findings.
  percentile: number | null;
  probability: number | null;
  // Adjudicator down-rank signal — missed/surfacer findings only; agreed carry
  // null (the comparison matrix has no adjudication). `report_mentions` /
  // `no_report_coverage` sink below the gallery divider, same as SurfacerPanel.
  predictedVerdict: AdjudicationVerdict | null;
  evidenceQuote: string | null;
}

export interface RetroSelection {
  findings: RetroFinding[];
  reportText: string;
}

// Same clinical bar the surfacer endpoint applies to the missed set — agreed
// findings are held to it too so the two verdict groups are comparable. These
// are the device-engine clinical_tier significant tiers (exact mirror of the
// demo-backend surfacer CATEGORIES_BILLABLE).
const SIGNIFICANT_CATEGORIES = new Set(["acute", "prompt_workup"]);

export function selectRetroFindings(
  surfacer: SurfacerResponse | null | undefined,
  comparison: ReporterComparisonResponse | null | undefined,
): RetroSelection {
  const missed: RetroFinding[] = (surfacer?.candidates ?? []).map((c) => ({
    verdict: "missed",
    condition: c.finding,
    category: c.category,
    topSlice: c.top_slice,
    // Missed = not addressed by the report. The endpoint sometimes carries a
    // near-miss quote in report_excerpt (e.g. a tangential mention that still
    // classified ai_only); we never highlight it — that would contradict the
    // "not addressed in this report" notice. The miss phrase carries the context.
    reportExcerpt: null,
    contextPhrase: c.miss_phrase,
    advancement: c.advancement,
    percentile: c.percentile ?? null,
    probability: null,
    predictedVerdict: c.predicted_verdict,
    evidenceQuote: c.evidence_quote,
  }));

  const agreed: RetroFinding[] = (comparison?.comparison_matrix ?? [])
    .filter((e) => {
      const cls = getClassification(e);
      // The agreed set is secondary (the stage opens missed-only), so unlike
      // ComparisonPanel we don't surface an Error in the UI — but a dropped Error
      // means a data-shape regression (missing/invalid recommended_threshold), so
      // it gets a stable warning rather than vanishing silently. See CLAUDE.md
      // "No silent OP fallback" + "Silent execution is a bug".
      if (cls instanceof Error) {
        console.warn(`[retroFindings] dropping comparison entry: ${cls.message}`);
        return false;
      }
      return cls === "agreement" && SIGNIFICANT_CATEGORIES.has(e.category ?? "");
    })
    .map((e) => ({
      verdict: "agreed",
      condition: e.finding,
      category: e.category,
      topSlice: e.ai.top_slices?.[0] ?? null,
      reportExcerpt: e.report.excerpt ?? null,
      contextPhrase: e.report.excerpt ?? "",
      advancement: null,
      percentile: null,
      probability: e.ai.probability,
      predictedVerdict: null,
      evidenceQuote: null,
    }));

  // Safety-net first: missed (OP advancement desc, then cohort percentile desc),
  // then agreed (probability desc).
  missed.sort(
    (a, b) =>
      (b.advancement ?? 0) - (a.advancement ?? 0) || (b.percentile ?? 0) - (a.percentile ?? 0),
  );
  agreed.sort((a, b) => (b.probability ?? 0) - (a.probability ?? 0));

  const reportText = surfacer?.original_report_text ?? comparison?.original_report_text ?? "";
  return { findings: [...missed, ...agreed], reportText };
}
